/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React, { createRef } from 'react';

import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import DateControl from 'components/Form/DateControl';
import Country from 'components/Form/Country';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';

import Dropdown from 'components/Form/Dropdown';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import {
  foreignBenefitTypes, foreignContactFrequencyOptions, foreignFinancialInterestOptions, foreignInterestOwnerOptions
} from 'constants/enums/foreignActivityOptions';
import PVQName from 'components/Form/PVQName';
import { ForeignActivitiesCurrentItemModel } from 'forms/sections/foreign/activities/current/types';
import { FieldModel } from 'forms/shared/types';
import { ITextarea } from 'components/Form/Textarea/Textarea';

interface Props extends Partial<ForeignActivitiesCurrentItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
}

export default class CurrentBenefit extends ValidationElement<Props> {
  obligatedExplanationRef = createRef<ITextarea>();

  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  update = (queue: Partial<ForeignActivitiesCurrentItemModel>) => {
    this.props.onUpdate({
      PersonType: this.props.PersonType,
      Name: this.props.Name,
      BenefitType: this.props.BenefitType,
      BenefitTypeExplanation: this.props.BenefitTypeExplanation,
      BenefitNature: this.props.BenefitNature,
      BenefitDate: this.props.BenefitDate,
      DollarValue: this.props.DollarValue,
      Reason: this.props.Reason,
      StartDate: this.props.StartDate,
      EndDate: this.props.EndDate,
      BenefitFrequency: this.props.BenefitFrequency,
      BenefitFrequencyExplanation: this.props.BenefitFrequencyExplanation,
      TotalBenefitsValue: this.props.TotalBenefitsValue,
      ReceivingReason: this.props.ReceivingReason,
      FuturePlans: this.props.FuturePlans,
      FuturePlansExplanation: this.props.FuturePlansExplanation,
      BenefitCountry: this.props.BenefitCountry,
      BenefitObligated: this.props.BenefitObligated,
      ObligatedExplanation: this.props.ObligatedExplanation,
      NotObligatedExplanation: this.props.NotObligatedExplanation,
      CountryFocused: this.props.CountryFocused,
      ...queue,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  }

  updatePersonType = (values: ForeignActivitiesCurrentItemModel['PersonType']) => {
    this.update({
      PersonType: values,
    });
  };

  updateName = (values: ForeignActivitiesCurrentItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateBenefitType = (values: ForeignActivitiesCurrentItemModel['BenefitType']) => {
    this.update({
      BenefitType: values,
    });
  };

  updateBenefitTypeExplanation = (values: ForeignActivitiesCurrentItemModel['BenefitTypeExplanation']) => {
    this.update({
      BenefitTypeExplanation: values,
    });
  };

  updateBenefitNature = (value: ForeignActivitiesCurrentItemModel['BenefitNature']) => {
    this.update({
      BenefitNature: value,
      BenefitDate: value.value === 'Yes' ? {} : this.props.BenefitDate,
      DollarValue: value.value === 'Yes' ? {} : this.props.DollarValue,
      Reason: value.value === 'Yes' ? {} : this.props.Reason,
      StartDate: value.value === 'No' ? {} : this.props.StartDate,
      EndDate: value.value === 'No' ? {} : this.props.EndDate,
      BenefitFrequency: value.value === 'No' ? {} : this.props.BenefitFrequency,
      TotalBenefitsValue: value.value === 'No' ? {} : this.props.TotalBenefitsValue,
      ReceivingReason: value.value === 'No' ? {} : this.props.ReceivingReason,
      FuturePlans: value.value === 'No' ? {} : this.props.FuturePlans,
      FuturePlansExplanation: value.value === 'No' ? {} : this.props.FuturePlansExplanation,
    });
  };

  updateBenefitDate = (values: ForeignActivitiesCurrentItemModel['BenefitDate']) => {
    this.update({
      BenefitDate: values,
    });
  };

  updateDollarValue = (values: ForeignActivitiesCurrentItemModel['DollarValue']) => {
    this.update({
      DollarValue: values,
    });
  };

  updateReason = (values: ForeignActivitiesCurrentItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateStartDate = (values: ForeignActivitiesCurrentItemModel['StartDate']) => {
    this.update({
      StartDate: values,
    });
  };

  updateEndDate = (values: ForeignActivitiesCurrentItemModel['EndDate']) => {
    this.update({
      EndDate: values,
    });
  };

  updateBenefitFrequency = (values: ForeignActivitiesCurrentItemModel['BenefitFrequency']) => {
    this.update({
      BenefitFrequency: values,
    });
  };

  updateBenefitFrequencyExplanation = (values: ForeignActivitiesCurrentItemModel['BenefitFrequencyExplanation']) => {
    this.update({
      BenefitFrequencyExplanation: values,
    });
  };

  updateTotalBenefitsValue = (values: ForeignActivitiesCurrentItemModel['TotalBenefitsValue']) => {
    this.update({
      TotalBenefitsValue: values,
    });
  };

  updateReceivingReason = (values: ForeignActivitiesCurrentItemModel['ReceivingReason']) => {
    this.update({
      ReceivingReason: values,
    });
  };

  updateFuturePlans = (values: ForeignActivitiesCurrentItemModel['FuturePlans']) => {
    this.update({
      FuturePlans: values,
    });
  };

  updateFuturePlansExplanation = (values: ForeignActivitiesCurrentItemModel['FuturePlansExplanation']) => {
    this.update({
      FuturePlansExplanation: values,
    });
  };

  updateBenefitCountry = (values: ForeignActivitiesCurrentItemModel['BenefitCountry']) => {
    this.update({
      BenefitCountry: values,
    });
  };

  updateBenefitObligated = (values: ForeignActivitiesCurrentItemModel['BenefitObligated']) => {
    this.obligatedExplanationRef.current?.clearValue();

    this.update({
      BenefitObligated: values,
      ObligatedExplanation: values?.value === 'No' ? {} as FieldModel : this.props.ObligatedExplanation, // Only clear if No was selected
      NotObligatedExplanation: values?.value === 'Yes' ? {} as FieldModel : this.props.NotObligatedExplanation, // Only clear if yes was selected
    });
  };

  updateObligatedExplanation = (values: ForeignActivitiesCurrentItemModel['ObligatedExplanation']) => {
    this.update({
      ObligatedExplanation: values,
      NotObligatedExplanation: {} as FieldModel,
    });
  };

  updateNotObligatedExplanation = (values: ForeignActivitiesCurrentItemModel['NotObligatedExplanation']) => {
    this.update({
      ObligatedExplanation: {} as FieldModel,
      NotObligatedExplanation: values,
    });
  };

  render() {
    const labelPrefix = 'foreign.pvq.activities.benefits.label';
    const attribute = `List.items[${this.props.$index}].Item`;
    const isOther = (a: string) => a === 'Other';

    return (
      <div className="current-benefit">
        <Field
          title={i18n.t(`${labelPrefix}.receiver`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PersonType`}
        >
          <Dropdown
            attribute={`${attribute}.PersonType`}
            name="person-type"
            onError={this.props.onError}
            onUpdate={this.updatePersonType}
            value={this.props.PersonType?.value}
            required={this.props.required}
          >
            {foreignInterestOwnerOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
        </Field>
        {this.props.PersonType?.value === 'Dependent children' && (
          <Field
            title={i18n.t(`${labelPrefix}.dependentChildName`)}
            attribute={`${attribute}.Name`}
          >
            <PVQName
              name="Name"
              {...this.props.Name}
              onUpdate={this.updateName}
              onError={this.handleNameError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Name`}
              showLastFirstMiddleNameOrder
            />
          </Field>
        )}

        <Field
          title={i18n.t(`${labelPrefix}.benefitType`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.BenefitType`}
        >
          <Dropdown
            attribute={`${attribute}.BenefitType`}
            onError={this.props.onError}
            onUpdate={this.updateBenefitType}
            value={this.props.BenefitType?.value}
            required={this.props.required}
          >
            {foreignBenefitTypes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
        </Field>

        {isOther(this.props.BenefitType?.value) && (
          <Field
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.BenefitTypeExplanation`}
          >
            <Textarea
              name="BenefitTypeExplanation"
              label={i18n.t(`${labelPrefix}.explain`)}
              className="benfit-type-explanation"
              {...this.props.BenefitTypeExplanation}
              onUpdate={this.updateBenefitTypeExplanation}
              onError={this.handleError}
              required={this.props.required}
              attribute="BenefitTypeExplanation"
            />
          </Field>
        )}
        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.BenefitNature`}
          exactAttribute
        >

          <Branch
            name="BenefitNature"
            className="BenefitNature no-margin-bottom"
            label={i18n.t(`${labelPrefix}.benefitNature`)}
            labelSize="h4"
            {...this.props.BenefitNature}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateBenefitNature}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.BenefitNature`}
            yesLabel={i18n.t(`${labelPrefix}.recurring`)}
            noLabel={i18n.t(`${labelPrefix}.oneTime`)}
          />
        </Field>
        {/* is the nature of benefit reoccurring - (one-time) */}
        {this.props.BenefitNature?.value === 'No' && (
          <><Field
            title={i18n.t(`${labelPrefix}.benefitReceivedDate`)}
            help="foreign.activities.benefit.oneTime.help.received"
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.BenefitDate`}
          >
            <DateControl
              name="BenefitDate"
              className="received"
              {...this.props.BenefitDate}
              hideDay
              onUpdate={this.updateBenefitDate}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.BenefitDate`}
            />
          </Field>

            <Field
              title={i18n.t(`${labelPrefix}.DollarValue`)}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DollarValue`}
            >
              <Currency
                name="DollarValue"
                className="DollarValue"
                {...this.props.DollarValue}
                min="0"
                onUpdate={this.updateDollarValue}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.DollarValue`}

              />
            </Field>

            <Field
              title={i18n.t(`${labelPrefix}.benefitReason`)}
              titleSize="label"
              adjustFor="textarea"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Reason`}
            >
              <Textarea
                name="Explanation"
                className="explanation"
                {...this.props.Reason}
                onUpdate={this.updateReason}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.Reason`}
              />
            </Field>
          </>
        )}

        {/* nature of benefit reoccurring */}

        {this.props.BenefitNature?.value === 'Yes' && (
          <><Field
            title={i18n.t(`${labelPrefix}.firstBenefitReceivedDate`)}
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.StartDate`}
          >
            <DateControl
              name="StartDate"
              className="received"
              {...this.props.StartDate}
              hideDay
              onUpdate={this.updateStartDate}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.StartDate`}
            />
          </Field>

            <Field
              title="When is this benefit expected to end? "
              titleSize="label"
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.EndDate`}
            >
              <DateControl
                name="EndDate"
                className="received"
                {...this.props.EndDate}
                hideDay
                onUpdate={this.updateEndDate}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.EndDate`}
              />
            </Field>

            <Field
              title="How often is this benefit received? "
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.BenefitFrequency`}
            >
              <Dropdown
                attribute={`${attribute}.BenefitFrequency`}
                name="benefit-frequency"
                onError={this.props.onError}
                onUpdate={this.updateBenefitFrequency}
                value={this.props.BenefitFrequency?.value}
                required={this.props.required}
              >
                {foreignContactFrequencyOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Dropdown>
            </Field>
            {this.props.BenefitFrequency?.value === 'Other' && (
              <Field
                title="Please explain."
                adjustFor="textarea"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.BenefitFrequencyExplanation`}
              >
                <Textarea
                  name="BenefitFrequencyExplanation"
                  className="BenefitFrequencyExplanation"
                  {...this.props.BenefitFrequencyExplanation}
                  onUpdate={this.updateBenefitFrequencyExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.BenefitFrequencyExplanation`}
                />
              </Field>
            )}

            {/* <Textarea
                                  name="OtherNatureExplanation"
                                  label={i18n.t(`${labelPrefix}.explain`)}
                                  className="financial-interest-type-explanation"
                                  {...this.props.OtherNatureExplanation}
                                  onUpdate={this.updateOtherNatureExplanation}
                                  onError={this.handleError}
                                  required={this.props.required}
                                  attribute="OtherNatureExplanation"
                                /> */}
            <Field
              title={i18n.t(`${labelPrefix}.totalBenefitsValue`)}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.TotalBenefitsValue`}
            >
              <Currency
                name="TotalBenefitsValue"
                className="TotalBenefitsValue"
                {...this.props.TotalBenefitsValue}
                min="0"
                onUpdate={this.updateTotalBenefitsValue}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.TotalBenefitsValue`}
              />
            </Field>

            <Field
              title={i18n.t(`${labelPrefix}.receivingReason`)}
              titleSize="label"
              adjustFor="textarea"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ReceivingReason`}
            >
              <Textarea
                name="ReceivingReason"
                className="Receiving-Reason"
                {...this.props.ReceivingReason}
                onUpdate={this.updateReceivingReason}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.ReceivingReason`}
              />
            </Field>
            <Branch
              name="FuturePlans"
              className="FuturePlans no-margin-bottom"
              label={i18n.t(`${labelPrefix}.futurePlans`)}
              labelSize="h4"
              {...this.props.FuturePlans}
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateFuturePlans}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.FuturePlans`}
            />
            <Field
              title={i18n.t(`${labelPrefix}.explain`)}
              titleSize="label"
              adjustFor="textarea"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.FuturePlansExplanation`}
            >
              <Textarea
                name="FuturePlansExplanation"
                className="FuturePlansExplanation"
                {...this.props.FuturePlansExplanation}
                onUpdate={this.updateFuturePlansExplanation}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.FuturePlansExplanation`}
              />
            </Field>

          </>
        )}
        <Field
          title={i18n.t(`${labelPrefix}.benefitCountry`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.BenefitCountry`}
        >
          <Country
            name="BenefitCountry"
            {...this.props.BenefitCountry}
            excludeUnitedStates
            onUpdate={this.updateBenefitCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.BenefitCountry`}
          />
        </Field>

        <Branch
          name="BenefitObligated"
          className="benefit-obligated no-margin-bottom"
          label={i18n.t(`${labelPrefix}.obligation`)}
          labelSize="h4"
          {...this.props.BenefitObligated}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateBenefitObligated}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.BenefitObligated`}

        />

        {this.props.BenefitObligated?.value === 'Yes' && (
        <Field
          title={i18n.t(`${labelPrefix}.explain`)}
          titleSize="label"
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ObligatedExplanation`}
        >
          <Textarea
            name="ObligatedExplanation"
            className="ObligatedExplanation"
            {...this.props.ObligatedExplanation}
            onUpdate={this.updateObligatedExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.ObligatedExplanation`}
            ref={this.obligatedExplanationRef}
          />
        </Field>
        )}
        {this.props.BenefitObligated?.value === 'No' && (
        <Field
          title={i18n.t(`${labelPrefix}.explain`)}
          titleSize="label"
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.NotObligatedExplanation`}
        >
          <Textarea
            name="NotObligatedExplanation"
            className="NotObligatedExplanation"
            {...this.props.NotObligatedExplanation}
            onUpdate={this.updateNotObligatedExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.NotObligatedExplanation`}
            ref={this.obligatedExplanationRef}
          />
        </Field>
        )}
      </div>
    );
  }
}

CurrentBenefit.defaultProps = {

  onUpdate: (queue) => { },
  CountryFocused: false,
  onError: (value, arr: ErrorCode) => arr,

  PersonType: {},
  Name: {},
  BenefitType: {},
  BenefitTypeExplanation: {},
  BenefitNature: {},
  BenefitDate: {},
  DollarValue: {},
  Reason: {},
  StartDate: {},
  EndDate: {},
  BenefitFrequency: {},
  BenefitFrequencyExplanation: {},
  TotalBenefitsValue: {},
  ReceivingReason: {},
  FuturePlans: {},
  FuturePlansExplanation: {},
  BenefitCountry: {},
  BenefitObligated: {},
  ObligatedExplanation: {},
  NotOblgatedExplanation: {},
};
